import { ApolloLink, from, Operation, split } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import getConfig from 'next/config';
import { LoggerFactory } from '@cvent/logging/LoggerFactory';
import {
  createAttendeeHubWebSocketLink,
  createVideoMeetingWebSocketLink,
  httpLink,
  terminateLink,
  videoMeetingLink
} from './links';

const LOG = LoggerFactory.create('apollo/createGraphLink');

const { publicRuntimeConfig } = getConfig();

function isSubscription(operation: Operation): boolean {
  const definition = getMainDefinition(operation.query);
  return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
}

export const createGraphLink = (eventId: string, attendeeId: string, isAuthExpired: boolean): ApolloLink => {
  const createWebsocketConnection =
    typeof window !== 'undefined' && // WebSocketLink can only be configured on the client
    !isAuthExpired; // Force close Websocket connection on isAuthExpired

  const websocketUri = String(publicRuntimeConfig.GRAPHQL_URI_EXTERNAL)
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  const subscriptionLink = createWebsocketConnection
    ? createAttendeeHubWebSocketLink(eventId, attendeeId, websocketUri)
    : null;

  const videoMeetingWebsocketUri = String(publicRuntimeConfig.VIDEO_MEETING_URL)
    .replace('https://', 'wss://')
    .replace('http://', 'ws://');
  const videoMeetingSubscriptionLink = createWebsocketConnection
    ? createVideoMeetingWebSocketLink(eventId, attendeeId, videoMeetingWebsocketUri)
    : null;

  LOG.debug('Creating GraphQL links', { createWebsocketConnection });
  return from(
    [
      // Split to /video-meeting-subscription
      createWebsocketConnection
        ? split(
            operation => isSubscription(operation) && operation.getContext().clientName === 'video-meeting',
            videoMeetingSubscriptionLink
          )
        : null,

      // Split to /video-meeting
      split(
        operation => !isSubscription(operation) && operation.getContext().clientName === 'video-meeting',
        videoMeetingLink
      ),

      // Split to WebSocketLink for subscriptions
      createWebsocketConnection ? split(operation => isSubscription(operation), subscriptionLink) : null,

      // Terminate any subscription operations when no other websocket links are available
      // This likely means the user is not authenticated or this isn't running in the browser
      split(operation => isSubscription(operation), terminateLink),

      httpLink
    ].filter(Boolean)
  );
};
